import { useCallback, useEffect, useRef, useState } from 'react';
import Client from '../../../../model/api/Client';
import { SuperSelect } from '../../../superSelect/SuperSelect';
import { Submit } from '../../../form/Submit';
import AppCookies from '../../../../model/cookies/AppCookies';

interface IProps {
	onSuccess: (email: string, slug: string) => void;
	apiClient: Client;
	defaultSlug: string;
	companyName: string;
}

export const Step1 = ({ onSuccess, apiClient, defaultSlug, companyName }: IProps) => {
	const [email, setEmail] = useState('');
	const [slug, setSlug] = useState<string>(defaultSlug);
	const [isEmailError, setIsEmailError] = useState(false);
	const [isCompanyError, setIsCompanyError] = useState(false);
	const [isError, setIsError] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const onEmailChange = useCallback((e: any) => setEmail(e.target.value), [setEmail]);

	const onSubmitEmail = useCallback(
		(callback?: () => void | null) => {
			if (email === '' || slug === '') {
				setIsEmailError(email === '');
				setIsCompanyError(slug === '');
				callback && callback();
				return;
			}

			setIsEmailError(false);
			setIsCompanyError(false);

			apiClient
				.validationEmail({ email, slug })
				.then((response: any) => {
					if (response.result.success === false) {
						for (const key of Object.keys(response.result.errors)) {
							const error = response.result.errors[key];

							if (error.type === 'company') {
								setIsCompanyError(true);
							}

							if (error.type === 'email') {
								setIsEmailError(true);
							}
						}

						callback && callback();
						return;
					}

					AppCookies.saveUserEmail(email);
					onSuccess(email, slug);
				})
				.catch(() => {
					setIsError(true);
					callback && callback();
				});
		},
		[email, slug, onSuccess, apiClient, setIsEmailError, setIsCompanyError, setIsError]
	);

	useEffect(() => {
		const handleKeyboardKey = (event: KeyboardEvent) => {
			if (event.target === inputRef.current) {
				if (event.key === 'Enter') {
					onSubmitEmail();
				}
			}
		};

		document.addEventListener('keydown', handleKeyboardKey, true);
		return () => {
			document.removeEventListener('keydown', handleKeyboardKey, true);
		};
	}, [onSubmitEmail, inputRef]);

	const onCompanySearch = useCallback(
		(search: string, setList: any) => {
			const onSuccessSearch = (response: any) => (response !== null ? setList(response.result) : null);
			setSlug('');
			search === '' ? setList([]) : apiClient.companySearch(search, 10).then(onSuccessSearch);
		},
		[apiClient]
	);

	const formatCompanyItem = useCallback(
		(item: any, defaultOnClick: any) => (
			<a
				className="dropdown-item"
				// eslint-disable-next-line react/jsx-no-bind
				onClick={() => {
					setSlug(item.slug);
					setIsCompanyError(false);
					defaultOnClick(item.name);
				}}>
				{item.name}
			</a>
		),
		[]
	);

	return (
		<section>
			<div className={'container-lg container-lg-max pt-5'}>
				<div className="row">
					<div className="col-md-6 col-lg-5 col-xl-5 form-group required">
						{companyName === '' ? (
							<>
								<h2>Koho budete hodnotit?</h2>
								<p>Které firmě udělíte pochvalu a která dostane za uši?</p>
							</>
						) : (
							<>
								<h2>{companyName}</h2>
								<p>K hodnocení firmy už stačí jen zadat vaši e&#8209;mailovou adresu.</p>
							</>
						)}

						{defaultSlug === '' ? (
							<div className={'form-group required pb-5'}>
								<label htmlFor={'company-search-add-review'} className={'form-label control-label h5'}>
									Název hodnocené firmy
								</label>
								<SuperSelect
									defaultValue={slug ? slug : ''}
									inputConfig={{
										className: 'form-control' + (isCompanyError ? ' is-invalid' : ''),
										placeholder: 'Např. Coweo Technologies',
										autoComplete: 'off',
										id: 'company-search-add-review'
									}}
									wrapperConfig={{
										className: isCompanyError ? ' is-invalid' : ''
									}}
									onSearch={onCompanySearch}
									formatItem={formatCompanyItem}
								/>
								{isCompanyError ? (
									<span className={'invalid-feedback'}>Vyberte firmu z nabídky</span>
								) : null}
							</div>
						) : null}
						<div className={'form-group required pb-5'}>
							<label htmlFor={'review-user-email'} className={'form-label control-label h5'}>
								Váš e-mail
							</label>
							<div className="form-text pb-2">
								Vaše hodnocení je anonymní, na e&#8209;mail vám jen zašleme potvrzení, že souhlasíte se
								zveřejněním recenze.
							</div>
							<input
								id={'review-user-email'}
								type={'email'}
								className={'form-control' + (isEmailError || isError ? ' is-invalid' : '')}
								placeholder={'E-mailová adresa'}
								autoComplete={'off'}
								value={email}
								onChange={onEmailChange}
								ref={inputRef}
							/>
							{isEmailError ? <span className={'invalid-feedback'}>Zadaný email není platný</span> : null}
							{isError ? (
								<span className={'invalid-feedback'}>Spojení se nezdařilo, zkuste to prosím znovu</span>
							) : null}
							<div className={'form-row mt-5'}>
								<Submit
									text={'Přejít k hodnocení'}
									className={'btn btn-primary btn-xl'}
									onClick={onSubmitEmail}
								/>
							</div>
						</div>
					</div>
					<div className={'col-md-6 col-lg-7 col-xl-7 d-block order-lg-last'}>
						<div className={'d-flex flex-column justify-content-end text-center w-100 h-100'}>
							<img
								src={'/review-img/entry-bg.png'}
								alt={'Koho budete hodnotit ?'}
								className={'mx-auto mw-100'}
								style={{ maxWidth: '100%', height: 'auto' }} // Prevent scaling up
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
